import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, Star, User, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  author_name: string;
  content: string;
  rating: number;
  created_at: string;
}

const ROTATE_MS = 6000;

const TestimonialRotator = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from("reviews")
        .select("id, author_name, content, rating, created_at")
        .eq("status", "approved")
        .order("created_at", { ascending: false })
        .limit(20);
      if (!cancelled && data) setReviews(data as Review[]);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % reviews.length);
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  const review = reviews[index % reviews.length];

  return (
    <section
      id="testimonials"
      aria-label="Community testimonials from the WhiteHat Hub Wall of Love"
      className="relative px-6 py-16 scroll-mt-20 border-t border-border"
    >
      <div className="container mx-auto max-w-3xl relative z-10 text-center">
        <div className="flex items-center justify-center gap-2 mb-3 reveal-on-scroll">
          <Quote className="w-5 h-5 text-primary" aria-hidden="true" />
          <h2 className="text-2xl md:text-3xl font-bold">Wall of Love</h2>
        </div>
        <p className="text-muted-foreground text-sm mb-10 reveal-on-scroll">
          What our community members say about WhiteHat Hub
        </p>

        <figure key={review.id} className="section-animate">
          <blockquote className="text-base md:text-lg leading-relaxed text-foreground/90 max-w-2xl mx-auto">
            “{review.content}”
          </blockquote>
          <figcaption className="mt-6 flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" aria-hidden="true" />
            </div>
            <span className="font-semibold text-sm">{review.author_name}</span>
            <span className="flex gap-0.5" role="img" aria-label={`${review.rating} out of 5 stars`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-3 h-3 ${star <= review.rating ? "fill-primary text-primary" : "text-muted-foreground"}`}
                  aria-hidden="true"
                />
              ))}
            </span>
          </figcaption>
        </figure>

        {reviews.length > 1 && (
          <div className="flex items-center justify-center flex-wrap mt-6">
            {reviews.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show testimonial ${i + 1} of ${reviews.length}`}
                aria-current={i === index % reviews.length}
                className="inline-flex items-center justify-center min-w-11 min-h-11 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  aria-hidden="true"
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index % reviews.length ? "w-6 bg-primary" : "w-1.5 bg-muted-foreground/60"
                  }`}
                />
              </button>
            ))}
          </div>

        )}

        <div className="mt-8">
          <Link
            to="/wall-of-love"
            className="inline-flex items-center gap-2 text-sm font-medium link-underline"
          >
            Read all reviews
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TestimonialRotator;
